import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Upload, Database, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function DataBackupPage() {
  const { user, getAuthHeader } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/backup/summary`, { headers: getAuthHeader() });
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch summary');
    }
  };

  useEffect(() => {
    if (user?.is_owner || user?.allowed_modules?.includes('data_backup')) {
      fetchSummary();
    }
  }, []);

  const handleDownloadBackup = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/backup`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `backup_${new Date().toISOString().slice(0,10)}.json`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Backup downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download backup');
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreBackup = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Confirm before restore
    if (!window.confirm(
      '⚠️ WARNING: This will REPLACE all your current data!\n\n' +
      'Are you sure you want to restore from this backup?\n\n' +
      '(Your user accounts will be preserved)'
    )) {
      event.target.value = '';
      return;
    }

    setRestoring(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/admin/restore`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Backup restored successfully!');
      fetchSummary(); // Refresh the summary
      
      // Show details
      console.log('Restore result:', response.data);
    } catch (error) {
      toast.error('Failed to restore backup: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRestoring(false);
      event.target.value = '';
    }
  };

  if (!user?.is_owner && !user?.allowed_modules?.includes('data_backup')) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Card>
            <CardContent className="p-6 text-center">
              <AlertTriangle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
              <p>Only administrators can access data backup features.</p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Data Backup & Restore</h1>
          <p className="text-muted-foreground">
            Protect your data by creating backups before making changes or switching environments.
          </p>
        </div>

        {/* Current Environment Info */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Database className="w-5 h-5 text-blue-600" />
              <div>
                <p className="font-medium text-blue-900">Current Environment</p>
                <p className="text-sm text-blue-700">{window.location.hostname}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Download Backup Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="w-5 h-5 text-green-600" />
                Download Backup
              </CardTitle>
              <CardDescription>
                Save all your data to a file. Do this before switching environments!
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  <strong>What's included:</strong>
                </p>
                <ul className="text-sm text-green-700 mt-2 space-y-1">
                  <li>• Items, BOMs, Clients</li>
                  <li>• Sales Orders, Work Orders</li>
                  <li>• Indents, Purchase Orders</li>
                  <li>• Employees, Tasks, and more</li>
                </ul>
              </div>
              
              <Button 
                onClick={handleDownloadBackup} 
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700"
                size="lg"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Creating Backup...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download Backup File
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Restore Backup Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-orange-600" />
                Restore Backup
              </CardTitle>
              <CardDescription>
                Load data from a backup file into this environment.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm text-orange-800 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  <strong>Warning:</strong>
                </p>
                <ul className="text-sm text-orange-700 mt-2 space-y-1">
                  <li>• This will REPLACE existing data</li>
                  <li>• User accounts will be preserved</li>
                  <li>• Make sure you have a current backup first!</li>
                </ul>
              </div>
              
              <div className="relative">
                <input
                  type="file"
                  accept=".json"
                  onChange={handleRestoreBackup}
                  disabled={restoring}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <Button 
                  disabled={restoring}
                  variant="outline"
                  className="w-full border-orange-300 text-orange-700 hover:bg-orange-50"
                  size="lg"
                >
                  {restoring ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Restoring...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Select Backup File to Restore
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Data Summary */}
        {summary && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-blue-600" />
                Current Data Summary
              </CardTitle>
              <CardDescription>
                Total records: <strong>{summary.total_records.toLocaleString()}</strong>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {Object.entries(summary.collections).map(([name, count]) => (
                  <div key={name} className="bg-gray-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-gray-900">{count}</p>
                    <p className="text-xs text-gray-600 capitalize">
                      {name.replace(/_/g, ' ')}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Instructions */}
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle>How to Use</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-gray-400 mb-2">1</div>
                <p className="font-medium">Before Switching Environments</p>
                <p className="text-sm text-muted-foreground">
                  Click "Download Backup" to save all your data to a file.
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-gray-400 mb-2">2</div>
                <p className="font-medium">Save the File Safely</p>
                <p className="text-sm text-muted-foreground">
                  Keep the backup file on your computer or cloud storage.
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-2xl font-bold text-gray-400 mb-2">3</div>
                <p className="font-medium">Restore in New Environment</p>
                <p className="text-sm text-muted-foreground">
                  Use "Restore Backup" to load your data into the new environment.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
